import React, { useEffect, useState } from 'react';

interface CountdownTimerProps {
  expiresAt: number; // Unix seconds
  className?: string;
}

export const NO_EXPIRY_THRESHOLD_SECONDS = 365 * 24 * 60 * 60;

export function formatExpiry(expiresAt: number, nowSeconds = Math.floor(Date.now() / 1000)): string {
  const secondsLeft = Math.max(0, expiresAt - nowSeconds);
  if (secondsLeft <= 0) return '00:00';

  if (secondsLeft >= NO_EXPIRY_THRESHOLD_SECONDS) {
    return 'No expiry';
  }

  if (secondsLeft >= 24 * 60 * 60) {
    const date = new Date(expiresAt * 1000);
    const currentYear = new Date(nowSeconds * 1000).getFullYear();
    return date.toLocaleDateString([], {
      month: 'short',
      day: 'numeric',
      ...(date.getFullYear() !== currentYear ? { year: 'numeric' } : {}),
    });
  }

  if (secondsLeft >= 60 * 60) {
    const h = Math.floor(secondsLeft / 3600);
    const m = Math.floor((secondsLeft % 3600) / 60);
    return `${h}h ${m}m`;
  }

  const m = Math.floor(secondsLeft / 60);
  const s = secondsLeft % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export const CountdownTimer: React.FC<CountdownTimerProps> = ({ expiresAt, className = '' }) => {
  const [secondsLeft, setSecondsLeft] = useState(() => Math.max(0, expiresAt - Math.floor(Date.now() / 1000)));

  useEffect(() => {
    const tick = () => {
      const left = Math.max(0, expiresAt - Math.floor(Date.now() / 1000));
      setSecondsLeft(left);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [expiresAt]);

  const isUrgent = secondsLeft <= 300 && secondsLeft > 0;
  const isExpired = secondsLeft === 0;

  return (
    <span
      className={`font-mono-pp text-sm tabular-nums ${className}`}
      style={{
        fontFamily: 'var(--pp-mono)',
        color: isExpired ? 'var(--pp-danger)' : isUrgent ? 'var(--pp-warning)' : 'var(--pp-text-muted)',
        fontWeight: 500,
      }}
    >
      {formatExpiry(expiresAt)}
    </span>
  );
};

export default CountdownTimer;
